import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { View, Text, Switch, FormInput } from "@src/components";
import { Icon } from "@src/components/Icon";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useNotificationBlacklist } from "@visioni/a3s-notifications/app";
import { getBlacklistApi } from "@src/features/notifications";
import { useTenant, useTenantId, getTenantDisplayName, filterTenants } from "@src/features/tenants";
import { useCameras } from "@src/services/api/hooks";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { getCameraKeyString } from "@src/utils/key-parser";
import { AssetCameraKey, AssetId, CameraId } from "@visioni/a3s-schema";
import type { CameraApiResponse } from "@src/services/api/cameras-api";

type SiteSection = {
  siteId: string;
  title: string;
  cameras: CameraApiResponse[];
};

type CameraNotificationBlacklistProps = { enabled: boolean };

export function CameraNotificationBlacklist({ enabled }: CameraNotificationBlacklistProps) {
  const { theme } = useUnistyles();
  const { tenants, selectedTenant, selectTenant } = useTenant();
  const tenantId = useTenantId();
  const hasMultipleTenants = tenants.length > 1;

  const { data: cameras, isLoading: isCamerasLoading } = useCameras();
  const siteNames = useSiteNames();

  const blacklistApi = useMemo(() => getBlacklistApi(), []);
  const {
    blacklistedCameraKeys,
    isLoading: isBlacklistLoading,
    addToBlacklist,
    removeFromBlacklist,
  } = useNotificationBlacklist({
    blacklistApi,
    tenantId: tenantId || undefined,
    enabled,
  });

  const sites = useMemo<SiteSection[]>(() => {
    if (!cameras || cameras.length === 0) return [];
    const groups = groupCamerasBySite(cameras, "site", siteNames);
    return groups
      .map((group) => ({
        siteId: group.siteId,
        title: group.siteName,
        cameras: [...group.cameras].sort((a, b) => {
          const nameA = a.nickname ?? a.name ?? getCameraKeyString(a);
          const nameB = b.nickname ?? b.name ?? getCameraKeyString(b);
          return nameA.localeCompare(nameB);
        }),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [cameras, siteNames]);

  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const allFilteredTenants = useMemo(() => filterTenants(tenants, searchQuery), [tenants, searchQuery]);
  const slicedTenants = useMemo(() => allFilteredTenants.slice(0, MAX_VISIBLE_TENANTS), [allFilteredTenants]);
  const hasMoreTenants = allFilteredTenants.length > MAX_VISIBLE_TENANTS;

  const isLoading = isCamerasLoading || isBlacklistLoading;

  return (
    <>
      {hasMultipleTenants && selectedTenant && (
        <View mb="xl" bg="surfaceCard" style={styles.tenantCard}>
          <Pressable
            onPress={() => {
              setTenantPickerOpen((prev) => !prev);
              setSearchQuery("");
            }}
          >
            <View style={styles.tenantHeader}>
              <View flex={1}>
                <Text textStyle="xs/regular" color="textMuted">
                  Organisation
                </Text>
                <Text textStyle="sm/medium">{getTenantDisplayName(selectedTenant)}</Text>
              </View>
              <Icon
                name={tenantPickerOpen ? "chevronUp" : "chevronDown"}
                size={theme.spacing["7xl"]}
                color={theme.colors.textMuted}
              />
            </View>
          </Pressable>

          {tenantPickerOpen && (
            <>
              <View style={styles.tenantSearch}>
                <FormInput
                  placeholder="Search organisations"
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  size="medium"
                  clearable
                />
              </View>

              <ScrollView style={styles.tenantList} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
                {slicedTenants.map((tenant) => (
                  <Pressable
                    key={tenant.id}
                    onPress={() => {
                      selectTenant(tenant.id);
                      setTenantPickerOpen(false);
                      setSearchQuery("");
                    }}
                  >
                    <View style={[styles.tenantOption, tenant.id === tenantId && styles.tenantOptionActive]}>
                      <View flex={1}>
                        <Text textStyle="sm/regular">{getTenantDisplayName(tenant)}</Text>
                      </View>
                      {tenant.id === tenantId && (
                        <Icon
                          name="checkmark"
                          size={theme.spacing["5xl"]}
                          color={theme.colors.interactive.buttons.default_background}
                        />
                      )}
                    </View>
                  </Pressable>
                ))}

                {slicedTenants.length === 0 && (
                  <View py="3xl" alignItems="center">
                    <Text textStyle="sm/regular" color="textMuted">
                      No organisations found
                    </Text>
                  </View>
                )}

                {hasMoreTenants && (
                  <View py="2xl" alignItems="center">
                    <Text textStyle="xs/regular" color="textMuted">
                      Refine your search to view more
                    </Text>
                  </View>
                )}
              </ScrollView>
            </>
          )}
        </View>
      )}

      {isLoading ?
        <View py="4xl" alignItems="center">
          <ActivityIndicator color={theme.colors.text as string} />
        </View>
      : sites.map((site) => (
          <SiteCard
            key={site.siteId}
            site={site}
            tenantId={tenantId}
            blacklistedCameraKeys={blacklistedCameraKeys}
            onAdd={addToBlacklist}
            onRemove={removeFromBlacklist}
          />
        ))
      }
    </>
  );
}

type SiteCardProps = {
  site: SiteSection;
  tenantId: string;
  blacklistedCameraKeys: Set<string>;
  onAdd: (params: { tenantId: string; siteId: string; assetCameraKey: string }) => void;
  onRemove: (params: { tenantId: string; siteId: string; assetCameraKey: string }) => void;
};

function SiteCard({ site, tenantId, blacklistedCameraKeys, onAdd, onRemove }: SiteCardProps) {
  const { theme } = useUnistyles();
  const [expanded, setExpanded] = useState(true);

  return (
    <View mb="xl" bg="surfaceCard" style={styles.siteCard}>
      <Pressable onPress={() => setExpanded((prev) => !prev)}>
        <View style={styles.siteHeader}>
          <View flex={1}>
            <Text textStyle="sm/medium">{site.title}</Text>
            <Text textStyle="xs/regular" color="textMuted">
              {site.cameras.length} {site.cameras.length === 1 ? "camera" : "cameras"}
            </Text>
          </View>
          <Icon
            name={expanded ? "chevronUp" : "chevronDown"}
            size={theme.spacing["7xl"]}
            color={theme.colors.textMuted}
          />
        </View>
      </Pressable>

      {expanded &&
        site.cameras.map((camera) => (
          <CameraBlacklistRow
            key={getCameraKeyString(camera)}
            camera={camera}
            tenantId={tenantId}
            isBlacklisted={blacklistedCameraKeys.has(getCameraKeyString(camera))}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        ))}
    </View>
  );
}

type CameraBlacklistRowProps = {
  camera: CameraApiResponse;
  tenantId: string;
  isBlacklisted: boolean;
  onAdd: (params: { tenantId: string; siteId: string; assetCameraKey: string }) => void;
  onRemove: (params: { tenantId: string; siteId: string; assetCameraKey: string }) => void;
};

function CameraBlacklistRow({ camera, tenantId, isBlacklisted, onAdd, onRemove }: CameraBlacklistRowProps) {
  const cameraKey = getCameraKeyString(camera);
  const friendlyName = camera.nickname ?? camera.name;

  let shortKey = cameraKey;
  if (friendlyName) {
    try {
      const parsed = AssetCameraKey.fromString(cameraKey);
      shortKey = `${AssetId.unique(AssetCameraKey.asset(parsed))}@${CameraId.toString(AssetCameraKey.camera(parsed))}`;
    } catch {
      // Fall back to the full key if parsing fails
    }
  }

  const displayName = friendlyName ?? cameraKey;
  const isNotificationsEnabled = !isBlacklisted;

  const handleToggle = useCallback(() => {
    const params = { tenantId, siteId: camera.site.id, assetCameraKey: cameraKey };
    if (isNotificationsEnabled) {
      onAdd(params);
    } else {
      onRemove(params);
    }
  }, [tenantId, camera.site.id, cameraKey, isNotificationsEnabled, onAdd, onRemove]);

  return (
    <View bg="surfaceCard" style={styles.cameraRow}>
      <View flex={1}>
        <Text textStyle="sm/regular" numberOfLines={1}>
          {displayName}
        </Text>
        {friendlyName && (
          <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
            {shortKey}
          </Text>
        )}
      </View>
      <Switch active={isNotificationsEnabled} onToggle={handleToggle} size="medium" />
    </View>
  );
}

const MAX_VISIBLE_TENANTS = 20;
const TENANT_LIST_MAX_HEIGHT = 240;

const styles = StyleSheet.create((theme) => ({
  tenantCard: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: "hidden",
  },
  tenantHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["2xl"],
    paddingHorizontal: theme.spacing.xl,
  },
  tenantSearch: {
    paddingHorizontal: theme.spacing.xl,
    paddingBottom: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    paddingTop: theme.spacing.lg,
  },
  tenantList: {
    maxHeight: TENANT_LIST_MAX_HEIGHT,
  },
  tenantOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["2xl"],
    paddingHorizontal: theme.spacing.xl,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  tenantOptionActive: {
    backgroundColor: theme.colors.surfaceCard,
  },
  siteCard: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: "hidden",
  },
  siteHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["2xl"],
    paddingHorizontal: theme.spacing.xl,
  },
  cameraRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["2xl"],
    paddingHorizontal: theme.spacing.xl,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
}));
